import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, MapPin, Calendar, Clock, ChevronDown, CalendarDays, Newspaper, HelpCircle, LogIn, Camera, Images } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import Countdown from '@/components/Countdown';
import Guestbook from '@/components/Guestbook';
import { Button } from '@/components/ui/button';
import { getPhotosCount } from '@/lib/api';

const HomePage = () => {
  const [photosCount, setPhotosCount] = useState(0);

  useEffect(() => {
    getPhotosCount().then((res) => setPhotosCount(res.data?.count || 0)).catch(() => {});
  }, []);

  const scrollToContent = () => {
    document.getElementById('content')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <PublicLayout showNav={true}>
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden grain-overlay">
        {/* Background Image - Domaine de la Rose Blanche */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_a31999b5-4753-4cfc-9fe8-180dccde4d88/artifacts/t0zp5out_image.png"
            alt="Domaine de la Rose Blanche"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-charcoal/40 to-charcoal/60"></div>
        </div>

        {/* Guestbook Widget - Top Right */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="absolute top-24 right-4 z-20 hidden lg:block w-80"
        >
          <Guestbook />
        </motion.div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <span className="text-sm uppercase tracking-[0.3em] text-white/90 font-body">Nous nous marions</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-5xl md:text-7xl lg:text-8xl text-gold mb-4 drop-shadow-lg"
            style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}
            data-testid="hero-title"
          >
            Mélanie & Cédric
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex items-center justify-center gap-4 mb-12"
          >
            <div className="h-px w-16 bg-gold"></div>
            <Heart className="w-5 h-5 text-gold" fill="#D4AF37" />
            <div className="h-px w-16 bg-gold"></div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="font-accent text-2xl md:text-3xl text-white italic mb-8 drop-shadow-md"
          >
            5 septembre 2026
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mb-8"
          >
            <Countdown />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="text-white/90 text-base md:text-lg mb-6 max-w-xl mx-auto leading-relaxed font-semibold"
          >
            Partagez vos plus belles photos du grand jour et découvrez celles déposées par les autres invités.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col items-center gap-3"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/photos?action=deposer">
                <Button
                  data-testid="hero-photos-upload-btn"
                  className="btn-primary text-base"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Déposer mes photos
                </Button>
              </Link>
              <Link to="/photos?action=album">
                <Button
                  data-testid="hero-photos-album-btn"
                  variant="outline"
                  className="btn-secondary text-base bg-white/10 backdrop-blur-sm text-white border-white/70 hover:bg-white hover:text-terracotta"
                >
                  <Images className="w-5 h-5 mr-2" />
                  Découvrir l'album
                </Button>
              </Link>
            </div>
            <p className="text-white/70 text-sm font-semibold">
              Une surprise vous attend à chaque page...
            </p>
          </motion.div>

          {/* Quick Navigation - Mobile Only */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="md:hidden mt-8 w-full max-w-sm mx-auto"
          >
            <div className="grid grid-cols-2 gap-3">
              <Link to="/programme" className="block">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <CalendarDays className="w-6 h-6 text-terracotta mx-auto mb-2" />
                  <span className="text-sm font-medium text-charcoal">Programme</span>
                </div>
              </Link>
              <Link to="/actualites" className="block">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <Newspaper className="w-6 h-6 text-terracotta mx-auto mb-2" />
                  <span className="text-sm font-medium text-charcoal">Actualités</span>
                </div>
              </Link>
              <Link to="/faq" className="block">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <HelpCircle className="w-6 h-6 text-terracotta mx-auto mb-2" />
                  <span className="text-sm font-medium text-charcoal">FAQ</span>
                </div>
              </Link>
              <Link to="/espace-invite" className="block">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <LogIn className="w-6 h-6 text-terracotta mx-auto mb-2" />
                  <span className="text-sm font-medium text-charcoal">Connexion</span>
                </div>
              </Link>
              <Link to="/photos" className="block col-span-2" data-testid="home-mobile-photos-tile">
                <div className="rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-shadow text-white"
                  style={{ background: 'linear-gradient(135deg, #D4A373 0%, #B8862F 100%)' }}>
                  <Camera className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-semibold">Déposez vos photos ici !</span>
                </div>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          onClick={scrollToContent}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/80 hover:text-gold transition-colors"
          aria-label="Défiler vers le bas"
        >
          <ChevronDown className="w-8 h-8 animate-bounce" />
        </motion.button>
      </section>

      {/* Welcome Message */}
      <section id="content" className="section-padding bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm uppercase tracking-[0.3em] text-stone font-body block mb-4">Bienvenue</span>
            <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-8">
              Nous avons le plaisir de vous convier à notre mariage
            </h2>
            <p className="text-stone leading-relaxed mb-8">
              C'est avec une immense joie que nous vous invitons à partager ce moment unique avec nous.
              Votre présence serait le plus beau des cadeaux pour célébrer notre union.
            </p>
            <div className="flex items-center justify-center gap-2 text-terracotta">
              <span className="font-accent text-xl italic">Mélanie</span>
              <Heart className="w-4 h-4" fill="#D4A373" />
              <span className="font-accent text-xl italic">Cédric</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Info Cards */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card-elegant text-center"
            >
              <Calendar className="w-10 h-10 text-terracotta mx-auto mb-4" />
              <h3 className="font-display text-xl text-charcoal mb-2">La Date</h3>
              <p className="text-stone">Samedi 5 septembre 2026</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card-elegant text-center"
            >
              <Clock className="w-10 h-10 text-terracotta mx-auto mb-4" />
              <h3 className="font-display text-xl text-charcoal mb-2">L'Horaire</h3>
              <p className="text-stone">Cérémonie à 14h00</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-elegant text-center"
            >
              <MapPin className="w-10 h-10 text-terracotta mx-auto mb-4" />
              <h3 className="font-display text-xl text-charcoal mb-2">Le Lieu</h3>
              <p className="text-stone">Sainte-Eulalie</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Photos Souvenirs - Gros bouton */}
      <section className="section-padding bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm uppercase tracking-[0.3em] text-terracotta font-body block mb-4">Nos souvenirs</span>
            <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-4">
              Partagez vos photos du mariage
            </h2>
            <p className="text-stone leading-relaxed mb-10 max-w-xl mx-auto">
              Vous avez capturé un joli moment ? Déposez vos photos et découvrez celles des autres invités dans notre album commun.
            </p>

            <Link to="/photos" className="inline-block group" data-testid="home-photos-badge">
              <motion.div
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.97 }}
                className="relative w-52 h-52 md:w-60 md:h-60 mx-auto"
              >
                <span className="absolute inset-0 rounded-full bg-terracotta/30 animate-ping [animation-duration:2.5s]"></span>
                <span className="absolute -inset-2 rounded-full border-2 border-dashed border-gold/60 group-hover:rotate-45 transition-transform duration-700"></span>
                <div
                  className="relative w-full h-full rounded-full flex flex-col items-center justify-center text-white shadow-2xl shadow-orange-900/30 border-4 border-white"
                  style={{ background: 'linear-gradient(145deg, #E0B285 0%, #D4A373 45%, #B8862F 100%)' }}
                >
                  <Camera className="w-12 h-12 md:w-14 md:h-14 mb-2 drop-shadow" />
                  <span className="font-display text-lg md:text-xl font-bold leading-tight drop-shadow px-6">
                    Déposez vos photos ici !
                  </span>
                </div>
                {photosCount > 0 && (
                  <span
                    className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white text-terracotta border-2 border-gold/50 rounded-full px-4 py-1.5 text-sm font-semibold shadow-lg whitespace-nowrap flex items-center gap-1.5"
                    data-testid="home-photos-count-pill"
                  >
                    <Images className="w-4 h-4" />
                    {photosCount} photo{photosCount > 1 ? 's' : ''} déjà partagée{photosCount > 1 ? 's' : ''}
                  </span>
                )}
              </motion.div>
            </Link>

            <div className="mt-10">
              <Link to="/photos" className="inline-flex items-center gap-2 text-terracotta hover:text-gold-dark font-medium transition-colors" data-testid="home-photos-album-link">
                <Images className="w-5 h-5" />
                Ou découvrez l'album des invités
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guestbook Section - Mobile Only (Desktop has it in hero) */}
      <section className="section-padding bg-gradient-to-b from-bone to-white lg:hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Left: Message to guests */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm uppercase tracking-[0.3em] text-terracotta font-body block mb-4">Un mot pour nous</span>
              <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-6">
                Juste un mot...
              </h2>
              <p className="text-stone leading-relaxed mb-4">
                Avant le grand jour, nous serions ravis de lire vos petits mots, vos souhaits 
                et vos encouragements. C'est aussi l'occasion de partager un souvenir 
                ou une anecdote que vous avez avec nous !
              </p>
              <p className="text-stone leading-relaxed">
                Vos messages seront précieusement conservés et nous les lirons avec émotion. 
                Merci de prendre ce moment pour nous écrire. 💕
              </p>
            </motion.div>

            {/* Right: Guestbook Widget */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Guestbook />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Section */}
      <section className="section-padding bg-terracotta-light/30">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-8">
              Découvrez tous les détails
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/programme">
                <Button data-testid="programme-btn" variant="outline" className="btn-secondary w-full sm:w-auto">
                  Programme & Lieux
                </Button>
              </Link>
              <Link to="/photos">
                <Button data-testid="home-bottom-photos-btn" className="btn-primary w-full sm:w-auto">
                  Voir les photos
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Venue Preview */}
      <section className="section-padding bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-sm uppercase tracking-[0.3em] text-stone font-body block mb-4">Le lieu de réception</span>
            <h2 className="font-display text-3xl md:text-4xl text-charcoal">
              Un cadre exceptionnel
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden shadow-xl"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_a31999b5-4753-4cfc-9fe8-180dccde4d88/artifacts/t0zp5out_image.png"
              alt="Domaine de la Rose Blanche"
              className="w-full h-64 md:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent flex items-end">
              <div className="p-6 md:p-10 text-white">
                <h3 className="font-display text-2xl md:text-3xl mb-2">Domaine de la Rose Blanche</h3>
                <p className="text-white/80">Un lieu magique pour une journée inoubliable</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default HomePage;
